/**
 * t302u: attendance register marks through the offline outbox (docs/offline.md). A tap made with no signal (or whose
 * send hit a network error / 5xx / 429) is saved on the device with the device time of the tap and a client_mark_id,
 * shown straight away (shared applyPendingMarks), and sent when there's signal, oldest first, each batch with its op id
 * as the Idempotency-Key (so neither a resend nor a lost answer records a mark twice). A refusal (register already
 * submitted, not yours any more) waits for the person with the reason; it is never applied over the server's marks.
 */
import { ApiError, classifyOutcome, isInFlightConflict, outboxRetryDelayMs, type ChecklistRunDetail, type PendingMark, type RegisterStatus } from '@tradeoutlet/shared';
import { dataApi } from './client';
import { createOutboxStore, type KeyValueStore, type OutboxStore } from './unsent';

export const REGISTER_OP_KINDS = ['register.mark'] as const;
/** Marks older than this are dropped from the device: a register from last week is not worth sending. */
export const REGISTER_OP_TTL_MS = 3 * 24 * 3600_000;

export interface RegisterOpMark { user_id: string; status: RegisterStatus; arrived_at?: string | null; note?: string | null; client_mark_id: string; device_marked_at: string }
export interface RegisterOutboxOp {
  id: string; // = Idempotency-Key
  kind: 'register.mark';
  userId: string;
  workspaceId: string;
  runId: string;
  /** "Attendance register · 3 marks", for the Waiting to send sheet. */
  label: string;
  createdAt: string;
  attempts: number;
  state: 'queued' | 'sending' | 'failed' | 'conflict';
  nextAt?: number;
  error?: string;
  marks: RegisterOpMark[];
}
export type NewRegisterOp = Pick<RegisterOutboxOp, 'workspaceId' | 'runId' | 'label' | 'marks'> & { id?: string };

export interface RegisterOutboxOptions {
  store: OutboxStore<RegisterOutboxOp>;
  uuid: () => string;
  isOnline?: () => boolean;
  now?: () => number;
  rand?: () => number;
  /** A sent op's answer (the run as the server now has it), or null when it was discarded. */
  onSent?: (op: RegisterOutboxOp, run: ChecklistRunDetail | null) => void;
}
export interface RegisterOutbox {
  readonly userId: string | null;
  load(userId: string): Promise<void>;
  ops(): RegisterOutboxOp[];
  subscribe(fn: () => void): () => void;
  enqueue(op: NewRegisterOp): Promise<RegisterOutboxOp>;
  flush(): Promise<void>;
  retry(id: string): Promise<void>;
  discard(id: string): Promise<void>;
  forget(): void;
}

export const registerOutboxStore = (open: (userId: string) => KeyValueStore<RegisterOutboxOp>) =>
  createOutboxStore<RegisterOutboxOp>(open, { kinds: REGISTER_OP_KINDS, ttlMs: REGISTER_OP_TTL_MS });

/** The taps still waiting for one run, oldest first, for applyPendingMarks (refused ones are not applied). */
export function pendingMarksFor(ops: RegisterOutboxOp[], runId: string): PendingMark[] {
  return ops.filter(o => o.runId === runId && o.state !== 'failed' && o.state !== 'conflict')
    .flatMap(o => o.marks.map(m => ({ user_id: m.user_id, status: m.status, arrived_at: m.arrived_at ?? null, note: m.note, marked_at: m.device_marked_at })));
}

type Outcome = { kind: 'sent'; run: ChecklistRunDetail } | { kind: 'retry'; error: string; delay?: number } | { kind: 'failed' | 'conflict'; error: string };
export function registerOutcomeOf(e: unknown): Outcome {
  const status = e instanceof ApiError ? e.status : null;
  const msg = e instanceof Error ? e.message : String(e);
  const c = classifyOutcome(status);
  if (c === 'retry') return { kind: 'retry', error: status == null ? 'No signal' : msg };
  if (c === 'conflict' && isInFlightConflict(`${msg} ${JSON.stringify(e instanceof ApiError ? e.body ?? '' : '')}`)) return { kind: 'retry', error: 'Still sending', delay: 2000 };
  return { kind: c === 'conflict' ? 'conflict' : 'failed', error: msg };
}

export function createRegisterOutbox(o: RegisterOutboxOptions): RegisterOutbox {
  const now = o.now ?? Date.now;
  const online = o.isOnline ?? (() => true);
  let userId: string | null = null;
  let ops: RegisterOutboxOp[] = [];
  let snapshot: RegisterOutboxOp[] = [];
  const listeners = new Set<() => void>();
  let flushing: Promise<void> | null = null;
  let again = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const emit = () => { snapshot = ops.map(x => ({ ...x })); listeners.forEach(f => f()); };
  const drop = async (op: RegisterOutboxOp) => { ops = ops.filter(x => x.id !== op.id); await o.store.remove(op.userId, op.id); };
  const ordered = () => [...ops].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const schedule = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    const waits = ops.filter(x => x.state === 'queued' && x.nextAt && x.nextAt > now()).map(x => x.nextAt!);
    if (!waits.length) return;
    timer = setTimeout(() => { timer = null; void api.flush(); }, Math.max(50, Math.min(...waits) - now()));
  };
  async function send(op: RegisterOutboxOp): Promise<Outcome> {
    try { return { kind: 'sent', run: await dataApi().ops.markRegister(op.workspaceId, op.runId, op.marks, op.id) }; }
    catch (e) { return registerOutcomeOf(e); }
  }
  async function runFlush() {
    if (!online()) return;
    for (const op of ordered()) {
      if (op.state === 'failed' || op.state === 'conflict') continue; // a refused batch waits for the person; later runs still go
      if (op.nextAt && op.nextAt > now()) break;
      op.state = 'sending'; emit();
      const r = await send(op);
      if (r.kind === 'sent') { await drop(op); o.onSent?.(op, r.run); emit(); continue; }
      op.attempts += 1; op.error = r.error;
      if (r.kind === 'retry') { op.state = 'queued'; op.nextAt = now() + (r.delay ?? outboxRetryDelayMs(op.attempts, (o.rand ?? Math.random)())); await o.store.save(op); emit(); break; }
      op.state = r.kind; op.nextAt = undefined; await o.store.save(op); emit();
    }
  }
  const api: RegisterOutbox = {
    get userId() { return userId; },
    async load(u) {
      if (userId === u && ops.length) return;
      userId = u;
      ops = (await o.store.load(u, now())).map(x => (x.state === 'sending' ? { ...x, state: 'queued' as const } : x));
      emit();
      void api.flush();
    },
    ops: () => snapshot,
    subscribe(fn) { listeners.add(fn); return () => { listeners.delete(fn); }; },
    async enqueue(n) {
      if (!userId) throw new Error('Register outbox: no user loaded');
      const op: RegisterOutboxOp = { ...n, id: n.id || o.uuid(), kind: 'register.mark', userId, createdAt: new Date(now()).toISOString(), attempts: 0, state: 'queued' };
      ops = [...ops, op];
      await o.store.save(op); emit();
      void api.flush();
      return op;
    },
    flush() {
      if (flushing) { again = true; return flushing; }
      flushing = (async () => {
        try { do { again = false; await runFlush(); } while (again); }
        finally { flushing = null; schedule(); }
      })();
      return flushing;
    },
    async retry(id) {
      const op = ops.find(x => x.id === id);
      if (!op) return;
      op.state = 'queued'; op.nextAt = undefined; op.error = undefined;
      for (const x of ops) if (x.state === 'queued') x.nextAt = undefined;
      await o.store.save(op); emit();
      await api.flush();
    },
    async discard(id) {
      const op = ops.find(x => x.id === id);
      if (!op) return;
      await drop(op); emit();
      o.onSent?.(op, null);
      void api.flush();
    },
    forget() { if (timer) clearTimeout(timer); timer = null; ops = []; userId = null; emit(); },
  };
  return api;
}
